import { useState, useCallback, useEffect, useRef } from 'react';
import { favoritesService } from '../lib/favorites/favoritesService';
import type { FavoriteItem } from '../types/favorites';
import { logger } from '../lib/logger';

export const useFavorites = () => {
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);
  const lastUpdateRef = useRef<string>('');

  // Charger les favoris au montage et écouter les changements
  useEffect(() => {
    const loadFavorites = () => {
      const loadedFavorites = favoritesService.getFavorites();

      // Comparer avec la dernière version pour éviter les re-renders inutiles
      const currentUpdate = JSON.stringify(loadedFavorites.map(f => f.id));
      if (currentUpdate !== lastUpdateRef.current) {
        lastUpdateRef.current = currentUpdate;
        setFavorites(loadedFavorites);

        logger.debug('Favorites loaded', {
          category: 'Favorites',
          data: { count: loadedFavorites.length }
        });
      }
    };

    // Charger les favoris au montage
    loadFavorites();

    // Écouter les changements de localStorage
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'app_favorites') {
        loadFavorites();
      }
    };

    // Écouter les événements de storage
    window.addEventListener('storage', handleStorageChange);

    // Polling de secours pour les changements dans le même onglet (car storage event ne se déclenche pas dans le même onglet)
    const intervalId = setInterval(loadFavorites, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(intervalId);
    };
  }, []);

  // Supprimer un favori
  const removeFavorite = useCallback((itemId: string) => {
    favoritesService.removeFavorite(itemId);
    const updatedFavorites = favoritesService.getFavorites();

    // Mettre à jour immédiatement l'état et la référence
    lastUpdateRef.current = JSON.stringify(updatedFavorites.map(f => f.id));
    setFavorites(updatedFavorites);

    logger.debug('Favorite removed', {
      category: 'Favorites',
      data: { itemId }
    });
  }, []);

  return {
    favorites,
    removeFavorite
  };
};